import useFilteredVariantTraits from "@insite/client-framework/Common/Hooks/useFilteredVariantTraits";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import updateVariantSelection from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateVariantSelection";
// import { ProductModel, TraitValueModel, VariantDisplayTypeValues, VariantTraitModel } from "@insite/client-framework/Types/ApiModels"; // Store Custom Code
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import ProductDetailsVariantButton from "@insite/content-library/Widgets/ProductDetails/ProductDetailsVariantButton";
import ProductDetailsVariantDropdown from "@insite/content-library/Widgets/ProductDetails/ProductDetailsVariantDropdown";
import ProductDetailsVariantSwatchDropdown from "@insite/content-library/Widgets/ProductDetails/ProductDetailsVariantSwatchDropdown";
import ProductDetailsVariantSwatchGrid from "@insite/content-library/Widgets/ProductDetails/ProductDetailsVariantSwatchGrid";
import ProductDetailsVariantSwatchList from "@insite/content-library/Widgets/ProductDetails/ProductDetailsVariantSwatchList";
import Select, { SelectProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Custom Code - Start */
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import getColor from "@insite/mobius/utilities/getColor";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import Heading, { HeadingPresentationProps } from "@store/Library/Components/Toolkit/Heading";
import { productDetailsFormWrapperStyles } from "@store/Themes/common/mixins/productDetails";
import { formSmallGridProps } from "@store/Themes/common/props/form";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { swatchProps } from "@store/Themes/common/props/swatch";
/* Custom Code - End */

/* Store Custom Code - Start */
import { ProductModel, VariantDisplayTypeValues, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import { TraitValueModel } from "@store/Framework/Types/ApiModels";
import find from "lodash/find";
import flatten from "lodash/flatten";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import omitBy from "lodash/omitBy";
import uniq from "lodash/uniq";
/* Store Custom Code - End */

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasParentProductId;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => ({
    variantTraits: getProductState(state, ownProps.parentProductId).value?.variantTraits,
    variantChildren: getVariantChildrenDataView(state, ownProps.parentProductId).value,
    variantSelection: state.pages.productDetails.variantSelection,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    variantProductState: getProductState(state, state.pages.productDetails.selectedProductId),
    selectedProductId: state.pages.productDetails.selectedProductId,
    productInfosById: state.pages.productDetails.productInfosById, // Store Custom Code
});

const mapDispatchToProps = {
    updateVariantSelection,
};

export interface ProductDetailsVariantOptionsStyles {
    wrapper?: InjectableCss;
    // Custom Code
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    heading?: HeadingPresentationProps;
    clearLink?: LinkPresentationProps;
}

export const variantOptionsStyles: ProductDetailsVariantOptionsStyles = {
    wrapper: { css: productDetailsFormWrapperStyles() },
    heading: {
        itemOffset: 0.2,
        extendedStyles: {
            title: swatchProps.label,
        },
    },
    clearLink: linkGetActionLinkProps(),
    gridContainer: mergeProps<ProductDetailsVariantOptionsStyles["gridContainer"]>(formSmallGridProps.gridContainer, {
        css: css`
            padding: ${pxToRem(20)};
            border: ${pxToRem(1)} solid ${getColor("common.border")};
        `,
    }),
    gridItem: { width: 12 },
};

const styles = variantOptionsStyles;

const ProductDetailsVariantOptions: React.FC<Props> = ({
    variantChildren,
    variantSelection,
    updateVariantSelection,
    variantTraits,
    parentProductId,
    variantProductState,
    selectedProductId,
    productInfosById, // Store Custom Code
}) => {
    useEffect(() => {
        let newUrl = window.location.href.split("?option=")[0];
        if (variantProductState.value && variantProductState.value.isVariantParent) {
            window.history.replaceState(null, "", newUrl);
        } else if (variantProductState.value && variantProductState.value.childTraitValues) {
            newUrl = newUrl.concat("?option=", variantProductState.value.productNumber);
            window.history.replaceState(null, "", newUrl);
        }
    }, [selectedProductId]);

    /* Store Custom Code - Start */
    const messageTypeStock = "1";
    const messageTypeOan = "3";
    const messageTypeDeactivated = "2";
    const messageTypeDiscontinued = "d";

    const getProductInfoById = (id: string) => {
        if (!productInfosById) {
            return "";
        }
        return productInfosById[id];
    };

    const getAvailabilityMessageTypeById = (id: string) => {
        if (!productInfosById) {
            return messageTypeDeactivated;
        }
        const productInfos = productInfosById[id];
        const inventoryDto = productInfos?.inventory?.inventoryAvailabilityDtos;
        const variantProduct = find(variantChildren, child => child.id === id);
        const qtyOnHand = productInfos?.inventory?.qtyOnHand ?? 0;

        if (variantProduct) {
            if (variantProduct?.properties?.isDiscontinuedCustom?.toLowerCase() === "true" && qtyOnHand === 0) {
                return messageTypeDiscontinued;
            }
            if (variantProduct?.properties?.isOrderAsNeeded?.toLowerCase() === "true" && qtyOnHand === 0) {
                return messageTypeOan;
            }
        }
        if (!inventoryDto) {
            return messageTypeDeactivated;
        }
        return inventoryDto[0].availability?.messageType?.toString();
    };

    const updateStyleTraitCustomProperties = () => {
        if (!productInfosById || !variantTraits || (!currentSelection && !availableSelectionSlots)) {
            return;
        }

        let idsToDiscontinued: string[] = [];
        let idsToDeactivate: string[] = [];
        let idsToOAN: string[] = [];
        let idsToStock: string[] = [];

        if (availableSelectionSlots === 1 || (nbSelectionsMade && nbSelectionsMade >= availableSelectionSlots - 1)) {
            const childrenToDiscontinued: ProductModel[] = [];
            const childrenToDeactivate: ProductModel[] = [];
            const childrenToOAN: ProductModel[] = [];
            const childrenToStock: ProductModel[] = [];

            variantChildren?.forEach((child: ProductModel) => {
                const messageType = getAvailabilityMessageTypeById(child.id);
                switch (messageType) {
                    case messageTypeStock:
                        childrenToStock.push(child);
                        break;
                    case messageTypeOan:
                        childrenToOAN.push(child);
                        break;
                    case messageTypeDeactivated:
                        childrenToDeactivate.push(child);
                        break;
                    case messageTypeDiscontinued:
                        childrenToDiscontinued.push(child);
                        break;
                }
            });
            idsToDiscontinued = getFilteredStyleTraitValuesIds(childrenToDiscontinued);
            idsToDeactivate = getFilteredStyleTraitValuesIds(childrenToDeactivate);
            idsToOAN = getFilteredStyleTraitValuesIds(childrenToOAN);
            idsToStock = getFilteredStyleTraitValuesIds(childrenToStock);
        }
        setVariantTraitsStockStatus(idsToDiscontinued, idsToDeactivate, idsToStock, idsToOAN);
    };

    const getFilteredStyleTraitValuesIds = (filteredProductCollection: ProductModel[]) => {
        if (
            !filteredProductCollection ||
            !filteredProductCollection.length ||
            !variantTraits ||
            !currentSelection ||
            (!isSelectionOneNeeded && !isSelectionComplete)
        ) {
            return [];
        }

        let filteredStyleTraitValuesIds: string[] = []; // just a list of ids
        let productsFitCurrentSelection: ProductModel[] = []; // children go here

        // Simplified logic when only one option group
        if (availableSelectionSlots === 1) {
            productsFitCurrentSelection = filteredProductCollection;
            const styleValuesOfFittingProducts = productsFitCurrentSelection.map((product: ProductModel) => {
                return product.childTraitValues;
            });
            filteredStyleTraitValuesIds = uniq(map(flatten(styleValuesOfFittingProducts), "id"));
        }

        if (availableSelectionSlots > 1) {
            productsFitCurrentSelection = filteredProductCollection?.filter((child: ProductModel) => {
                const filteredStyleValues: any = [];
                child?.childTraitValues?.forEach((childTraitValue: any) => {
                    if (
                        variantSelection[childTraitValue?.styleTraitId] &&
                        variantSelection[childTraitValue?.styleTraitId] === childTraitValue?.id
                    ) {
                        filteredStyleValues.push(childTraitValue);
                    }
                });
                if (!filteredStyleValues.length) {
                    return false;
                }

                if (
                    isSelectionComplete &&
                    availableSelectionSlots > 2 &&
                    filteredStyleValues.length === availableSelectionSlots - 2
                ) {
                    return false;
                }

                if (isSelectionComplete) {
                    return filteredStyleValues.length >= isSelectionOneNeeded;
                }
                return filteredStyleValues.length === nbSelectionsMade;
            });

            const styleValuesOfFittingProducts = productsFitCurrentSelection.map((product: ProductModel) => {
                return product.childTraitValues;
            });

            filteredStyleTraitValuesIds = uniq(map(flatten(styleValuesOfFittingProducts), "id"));
        }

        return filteredStyleTraitValuesIds;
    };

    const setVariantTraitsStockStatus = (
        idsToDiscontinued: string[],
        idsToDeactivate: string[],
        idsToStock: string[],
        idsToOAN: string[],
    ) => {
        availableVariantTraits.forEach((variantTrait: VariantTraitModel) => {
            variantTrait?.traitValues?.forEach((traitValue: TraitValueModel) => {
                const applyStatus = (statusKey: string, condition: boolean) => {
                    if (condition) {
                        (traitValue as any)[statusKey] = true;
                    }
                };
                // Don't apply colors to selected items during partial selection
                if (variantSelection[variantTrait.id] === traitValue.id) {
                    if (isSelectionComplete && variantProductState.value) {
                        // If all options are selected we apply the product's styles
                        const messageType = getAvailabilityMessageTypeById(variantProductState.value.id);
                        applyStatus("isStock", messageType === messageTypeStock);
                        applyStatus("isOrderAsNeeded", messageType === messageTypeOan);
                        applyStatus("isDeactivated", messageType === messageTypeDeactivated);
                        applyStatus("isDiscontinued", messageType === messageTypeDiscontinued);
                    }
                } else {
                    // Apply colors when full selection
                    applyStatus("isDiscontinued", includes(idsToDiscontinued, traitValue.id.toString()));
                    applyStatus("isDeactivated", includes(idsToDeactivate, traitValue.id.toString()));
                    applyStatus("isStock", includes(idsToStock, traitValue.id.toString()));
                    applyStatus("isOrderAsNeeded", includes(idsToOAN, traitValue.id.toString()));
                }
            });
        });
    };
    /* Store Custom Code - End */

    const variantChangeHandler = (traitValueId: string, variantTraitId: string) => {
        updateVariantSelection({ traitValueId, variantTraitId, productId: parentProductId });
    };

    if (!variantTraits || variantTraits.length === 0 || !variantChildren) {
        return null;
    }

    const filteredVariantTraits = useFilteredVariantTraits(variantTraits, variantChildren, variantSelection, true);

    /* Custom Code - Start */
    const availableVariantTraits = filteredVariantTraits.filter(x => x.traitValues?.length);
    const clearVariantSelection = () => availableVariantTraits.forEach(x => variantChangeHandler("", x.id));
    /* Custom Code - End */

    /* Store Custom Code - Start */
    const availableSelectionSlots = variantTraits.length;
    const currentSelection = omitBy(variantSelection, isEmpty);
    const nbSelectionsMade = Object.keys(currentSelection).length;
    const isSelectionComplete = nbSelectionsMade === availableSelectionSlots;
    const isSelectionOneNeeded = nbSelectionsMade === availableSelectionSlots - 1;
    updateStyleTraitCustomProperties();
    /* Store Custom Code - End */

    const renderVariant = (variantTrait: VariantTraitModel) => {
        switch (variantTrait.displayType) {
            case VariantDisplayTypeValues.Button:
                return (
                    <ProductDetailsVariantButton
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                    />
                );
            case VariantDisplayTypeValues.SwatchDropdown:
                return (
                    <ProductDetailsVariantSwatchDropdown
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                    />
                );
            case VariantDisplayTypeValues.SwatchGrid:
                return (
                    <ProductDetailsVariantSwatchGrid
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                    />
                );
            case VariantDisplayTypeValues.SwatchList:
                return (
                    <ProductDetailsVariantSwatchList
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                    />
                );
            default:
                return (
                    <ProductDetailsVariantDropdown
                        key={variantTrait.id}
                        variantSelection={variantSelection}
                        updateVariantSelection={variantChangeHandler}
                        variantTrait={variantTrait}
                    />
                );
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {/* Custom Code - Start */}
            <Heading title={translate("Select options")} {...styles.heading}>
                <Link {...styles.clearLink} onClick={() => clearVariantSelection()}>
                    {translate("Clear Selection")}
                </Link>
            </Heading>
            <GridContainer {...styles.gridContainer}>
                {availableVariantTraits.map(variantTrait => (
                    <GridItem key={variantTrait.id} {...styles.gridItem}>
                        {renderVariant(variantTrait as VariantTraitModel)}
                    </GridItem>
                ))}
            </GridContainer>
            {/* Custom Code - End */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsVariantOptions)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
